<template lang="pug">
  .bar.relative
    .show-more.row.middle.center.pointer(v-if='resources.length > 3' @click='toggleMore')
      simple-svg.dark-gray-icon.dropdown(
        :class='{active: showMoreResources}'
        :filepath='dropdownIcon'
        width='18px'
        height='18px'
      )
    .header.row.middle.center.m-bottom-2.m-top-05(v-if='showMoreResources')
      h2.text-normal.dark-gray.progress-count {{ $t('krea.progressBar.completePercentage', {percentage: totalProgress}) }}
      .checkbox(@mouseenter='showPublishTooltip' @mouseleave='hidePublishTooltip' @click='showPublishModal')
        checkbox.checkbox.m-top-05(circle :checked='canPublish' v-tooltip.top='publishTooltipOptions')
    .container.row(:class='{showMoreResources}')
      h2.text-normal.dark-gray.progress-count(v-show='!showMoreResources')
        | {{ $t('krea.progressBar.completePercentage', {percentage: totalProgress}) }}
      v-popover.progress-container(
        @update:open='(status) => toggleContentMenu(status)'
        popoverClass='krea-actions-popover'
        ref='contentMenuPopover'
      )
        .column
          .progress.blue(:style='contentStyle')
          .row.middle.center.pointer
            h2.dark-gray.text-normal.text-center.m-top-05 {{ contentTypeTranslation }}
            simple-svg.dropdown-icon.dark-gray-icon.m-top-05.m-left-05(
              :filepath='dropdownIcon'
              width='18px'
              height='18px'
              :class='{active: showContentMenu}'
            )
        template(slot='popover')
          content-menu(@clickedField='hideContentMenu')
      v-popover.progress-container(
        :delay='{show: 100, hide: 0}'
        @apply-show='(status) => toggleResourceMenu(true, resource.resourceId)'
        @hide='(status) => toggleResourceMenu(false, resource.resourceId)'
        popoverClass='krea-actions-popover'
        :key='resource.resourceId'
        :ref='"resourceMenuPopover" + resource.resourceId'
        v-for='resource in showMoreResources ? resources : resources.slice(0, 4)'
      )
        .progress-container
          .progress.yellow(:style='resourceStyle(resource.resourceId)')
          .row.middle.center.pointer
            h2.dark-gray.text-normal.text-center.m-top-05 {{ $t('krea.progressBar.resource') }}
            simple-svg.dropdown-icon.dark-gray-icon.m-top-05.m-left-05(
              :filepath='dropdownIcon'
              width='18px'
              height='18px'
              :class='{active: showResourceMenu && activeResource === resource.resourceId}'
            )
        template(slot='popover')
          resource-menu(@clickedField='hideResourceMenu(resource.resourceId)' :resourceId='resource.resourceId')
      .progress-container.row.center.top(v-if='showAddNewResource')
        button.add-resource.row.middle(@click='createResource')
          h2.text-normal.bold {{ $t('krea.educationalContent.addNewResource') }}
          simple-svg.icon.white-icon.m-left-1.m4-top-1(:filepath='addIcon' width='10px' height='10px')
      .checkbox.self-start(@mouseenter='showPublishTooltip' @mouseleave='hidePublishTooltip' @click='showPublishModal')
        checkbox.checkbox.self-start(
          v-if='!showMoreResources'
          circle
          :checked='canPublish'
          v-tooltip.top='publishTooltipOptions'
        )
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { contentType } from '@/utils/constants'
import dropdownIcon from '@/assets/ic_chevron-down.svg'
import { contentRequiredFieldsList, getContentCompletedFields } from '@/constants/kreaEducationalContent'
import { getResourceCompletedFieldsAmount } from '@/constants/kreaResource'
import Checkbox from '@/components/Inputs/Checkbox'
import ContentMenu from './ContentMenu'
import ResourceMenu from './ResourceMenu'
import addIcon from '@/assets/ic_add.svg'
import { languagesCodesToString } from '../../../../constants/languages'
import { routes } from '../../../../router'
import { educationalProgramLanguageStatus } from '../../../../utils/constants'
import { i18n } from '../../../../main'
import {
  resourcePresentationRequiredFieldsList,
  resourceRequiredFieldsList,
  resourceStudentClassRequiredFieldsList,
  resourceStudentRequiredFieldsList,
  resourceTeacherClassRequiredFieldsList,
  resourceTeacherRequiredFieldsList
} from '../../../../constants/kreaResource'

export default {
  components: { Checkbox, ContentMenu, ResourceMenu },
  data() {
    return {
      dropdownIcon,
      addIcon,
      showContentMenu: false,
      showResourceMenu: false,
      showMoreResources: false,
      displayPublishTooltip: false,
      activeResource: null
    }
  },
  watch: {
    canPublish() {
      if (this.canPublish) {
        this.showPublishTooltip()
        setTimeout(this.hidePublishTooltip, 3000)
      }
    }
  },
  computed: {
    ...mapState({
      activeLanguage: state => state.kreaEducationalContent.activeLanguage,
      content: state => state.kreaEducationalContent.content,
      resource: state => state.kreaResource.resource,
      contentCompleteness: state => state.kreaEducationalContent.contentCompleteness,
      contentLanguageCompleteness: state => state.kreaEducationalContent.contentLanguageCompleteness
    }),
    showAddNewResource() {
      return this.isContentView && !this.content.educationalProgramResources.length
    },
    isContentView() {
      return this.$route.name === routes.kreaEducationalContent
    },
    availableLanguages() {
      return this.isContentView
        ? Object.keys(this.content.activatedLanguages)
        : Object.keys(this.resource.activatedEducationalProgramLanguages)
    },
    disapprovedLanguages() {
      return this.isContentView
        ? this.availableLanguages.filter(key => this.content.activatedLanguages[key] === educationalProgramLanguageStatus.hidden)
        : this.availableLanguages.filter(key => this.resource.activatedEducationalProgramLanguages[key] === educationalProgramLanguageStatus.hidden)
    },
    publishTooltipOptions() {
      return {
        content: this.canPublish ? '¡Listo para publicar!' : '¡Completa para poder publicar!',
        classes: 'krea-action-tooltip',
        show: this.displayPublishTooltip,
        trigger: 'manual'
      }
    },
    resources() {
      return this.contentCompleteness.resources
    },
    availableResources() {
      return this.content.educationalProgramResources.filter(res => languagesCodesToString[res.locale] === this.activeLanguage)
    },
    contentTypeTranslation() {
      const contentTypeName = Object.keys(contentType).find(key => this.contentCompleteness.contentType === contentType[key])
      return i18n.t(`krea.educationalContent.${contentTypeName}`)
    },
    contentProgress() {
      const completedFields = getContentCompletedFields(this.contentCompleteness).length
      return Math.floor(Number((100 * completedFields) / contentRequiredFieldsList.length))
    },
    totalProgress() {
      const resources = this.contentCompleteness.resources
      if (resources && resources.length) {
        const resourcesProgress = resources.map(res => Number(this.resourceProgress(res.resourceId)))
        const totalResourcesProgress = resourcesProgress.reduce((acc, prog) => acc + prog, 0)
        return Math.floor(Number((Number(this.contentProgress) + Number(totalResourcesProgress)) / (1 + resources.length)))
      } else {
        return Math.floor(Number(Number(this.contentProgress) / 2))
      }
    },
    canPublish() {
      return Number(this.totalProgress) === 100
    },
    contentStyle() {
      return {
        '--progress-width': `${this.contentProgress}%`
      }
    }
  },
  methods: {
    ...mapActions([
      'createKreaResourceByContent',
      'showElement'
    ]),
    showPublishModal() {
      if (this.canPublish) {
        this.showElement({
          element: 'kreaPublishContentModal',
          props: {
            contentId: this.isContentView ? this.content.id : this.resource.educationalProgramId,
            keywords: this.isContentView ? this.content.keywords : this.resource.educationalProgramKeywords,
            languages: this.disapprovedLanguages,
            completeness: this.contentLanguageCompleteness
          }
        })
      }
    },
    showPublishTooltip() {
      this.displayPublishTooltip = true
    },
    hidePublishTooltip() {
      this.displayPublishTooltip = false
    },
    resourceProgress(id) {
      const resource = this.contentCompleteness.resources.find(res => res.resourceId === id)
      const requiredFieldListAmount = resourceRequiredFieldsList.length +
        resourcePresentationRequiredFieldsList.length +
        resourceTeacherRequiredFieldsList.length +
        resourceStudentRequiredFieldsList.length +
        resourceTeacherClassRequiredFieldsList.length * (resource.classPlans.length || 1) +
        resourceStudentClassRequiredFieldsList.length * (resource.classPlans.length || 1)
      const completedFields = getResourceCompletedFieldsAmount(resource)
      return Math.floor(Number((100 * completedFields) / requiredFieldListAmount))
    },
    resourceStyle(id) {
      return {
        '--progress-width': `${this.resourceProgress(id)}%`
      }
    },
    toggleContentMenu(status) {
      this.showContentMenu = status
    },
    toggleResourceMenu(status, id) {
      if (status) {
        this.activeResource = id
      }
      this.showResourceMenu = status
    },
    hideContentMenu() {
      this.$refs.contentMenuPopover.hide()
    },
    hideResourceMenu(resourceId) {
      this.$refs['resourceMenuPopover' + resourceId][0].hide()
    },
    toggleMore() {
      this.showMoreResources = !this.showMoreResources
    },
    async createResource() {
      const { success, id } = await this.createKreaResourceByContent({
        locale: this.activeLanguage,
        contentId: this.content.id
      })
      if (success) {
        this.$router.push({
          name: routes.kreaResourcePresentation,
          params: { id },
          query: this.activeLanguage !== this.locale ? { locale: this.activeLanguage } : null
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'src/scss/variables/colors.scss';
@import 'src/scss/variables/sizes.scss';

.bar{
  min-height: 60px;
  padding: 12px 0;
  width: 100%;
  z-index: 1;
  position: sticky;
  background-color: $white;
  top: $navbar-height;
  border-bottom: 1px solid $light-gray;
  .container{
    width: 100%;
    margin: 0 auto;
    max-width: $content-size;
    gap: 10px;
    &.showMoreResources{
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 10px;
    }
  }
  .show-more{
    position: absolute;
    background-color: $white;
    width: 100px;
    height: 18px;
    border: 1px solid $light-gray;
    border-top: none;
    border-radius: 0 0 5px 5px;
    top: 100%;
    right: 50%;
    transform: translateX(50%);
    .dropdown{
      transition: transform .5s ease;
      &.active{
        transform: rotate(180deg);
      }
    }
  }
}

.add-resource{
  .icon{
    padding: 5px;
    background-color: $blue;
    border-radius: 50%;
  }
}

.checkbox{
  width: 20px;
}

.progress-count{
  width: 110px;
}

.progress-container{
  flex: 1;
}

.progress{
  height: 12px;
  border-radius: 6px;
  position: relative;
  overflow: hidden;
  &.blue{
    background-color: #{$blue}40;
    &::after{
      background-color: $blue;
    }
  }
  &.yellow{
    background-color: #{$yellow}40;
    &::after{
      background-color: $yellow;
    }
  }
  &::after{
    content: "";
    width: 100%;
    transform: scaleX(var(--progress-width));
    transition: transform .5s ease;
    transform-origin: left;
    height: 12px;
    position: absolute;
    left: 0;
    z-index: 3;
  }
}

.dropdown-icon{
  transition: transform .5s ease;
  &.active{
    transform: rotate(180deg);
  }
}

.tooltip{
  &.krea-progress-bar{
    margin: 0;
    .popover-inner{
      padding: 0;
      background-color: transparent;
    }
    .popover-arrow{
      display: none;
    }
  }
}
</style>
